import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import ProgressBar from '@wix/da-shared-react/pkg/ProgressBar';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import Flex from '@wix/da-ds/pkg/Flex';
import classnames from 'classnames';
import { PapiEditorText } from '@wix/da-papi-types';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import DonationViewer from '@wix/da-shared-react/pkg/DaEditorWrapper/Viewer/Donation';
import { isEditorTextEmpty } from '@wix/da-shared-react/pkg/DaEditorWrapper/utils';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import DonatePointsPopup from './DonatePointsPopup';

import s from './Donations.scss';
import { BiData, DonatePointsClickBiEvent } from '@wix/da-bi/pkg/events';

export interface Props {
  amount: number;
  className?: string;
  description?: string | PapiEditorText;
  pushPointsModal: (username: string, title: string) => void;
  donors: number;
  goal: number;
  isOwner: boolean;
  moduleId: number;
  moduleVersion: number;
  title?: string;
  userUuid: string;
  username: string;
}

export const Donations: React.FC<Props> = ({
  amount,
  className,
  description,
  donors,
  goal,
  isOwner,
  moduleId,
  moduleVersion,
  title,
  userUuid,
  username,
  pushPointsModal,
}) => {
  const { t } = useTranslation();
  const [showDonatePointsPopup, setShowDonatePointsPopup] = useState(false);
  const hideDonatePopup = useCallback(() => {
    setShowDonatePointsPopup(false);
  }, []);
  const openDonatePopup = useCallback(() => {
    setShowDonatePointsPopup(true);
  }, []);

  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const openDonatePopupIfLoggedIn = restrictToLoggedIn(
    ReferralType.DONATE_POINTS,
    openDonatePopup
  );

  let hasDescription = false;
  if (description) {
    hasDescription =
      typeof description === 'string'
        ? description.length > 0
        : !isEditorTextEmpty(description);
  }
  const percentage = goal ? (amount / goal) * 100 : 0;
  const hasReachedGoal = amount >= goal;
  return (
    <>
      <div
        className={classnames(
          s['root'],
          !hasDescription && s['no-description'],
          className
        )}
      >
        <div className={s['wrapper']}>
          {/* title and description */}
          {title && <div className={s['title']}>{title}</div>}
          {hasDescription && (
            <div className={s['description']}>
              {typeof description === 'string' ? (
                description
              ) : (
                <TextRenderer
                  textContent={description}
                  reactViewer={DonationViewer}
                  legacyTheme={'default'}
                  viewerKey={`donation-${moduleId}-${moduleVersion}`}
                />
              )}
            </div>
          )}

          {/* goal */}
          <Flex className={s['section-title']} alignItems="center">
            <span>{goal}</span>
            <PointsIcon className={s['points-icon']} />
          </Flex>
          <div className={s['section-description']}>
            {t('widgets.donations.goal')}
            {isOwner && !goal && (
              <span className={s['empty-goal']}>
                {t('widgets.donations.emptyGoal')}
              </span>
            )}
          </div>

          {/* donors */}
          <div className={s['section-title']}>{donors}</div>
          <div className={s['section-description']}>
            {t('widgets.donations.donors')}
          </div>

          {/* status */}
          <div className={s['section-title']}>
            {t('widgets.donations.status')}
          </div>
          <div
            className={classnames(
              s['section-description'],
              s['section-description-status']
            )}
          >
            {t('widgets.donations.status.progress', {
              progress: `${amount}/${goal}`,
            })}
          </div>

          <ProgressBar
            percentage={percentage}
            className={s['progressbar-root']}
          />

          {hasReachedGoal && (
            <div className={s['goal-reached']}>
              {t('widgets.donations.reached.thanks')}{' '}
              <span className={s['accent']}>
                {t('widgets.donations.reached.helped')}
              </span>
            </div>
          )}
        </div>
      </div>
      {!isOwner && (
        <div className={s['footer']}>
          <BiLink
            className={classnames(s['donate-button'])}
            onClick={openDonatePopupIfLoggedIn}
            biData={BiData<DonatePointsClickBiEvent>({
              evid: 286,
              recipientid: userUuid,
            })}
          >
            <span>{t('widgets.donations.button.donate')}</span>
          </BiLink>
        </div>
      )}
      {showDonatePointsPopup && (
        <DonatePointsPopup
          username={username}
          moduleId={moduleId}
          close={hideDonatePopup}
        />
      )}
    </>
  );
};

export default Donations;
